import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { persistSignupAttribution } from '@/lib/attribution';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isPro: boolean;
  subscriptionEnd: string | null;
  checkSubscription: (force?: boolean) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  isPro: false,
  subscriptionEnd: null,
  checkSubscription: async () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPro, setIsPro] = useState(false);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);

  const subInFlight = useRef<Promise<void> | null>(null);
  const subLastFetch = useRef<number>(0);
  const SUB_TTL_MS = 30_000;

  const checkSubscription = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && now - subLastFetch.current < SUB_TTL_MS) return;
    if (subInFlight.current) return subInFlight.current;

    const promise = (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('check-subscription');
        if (error) throw error;
        setIsPro(data?.subscribed ?? false);
        setSubscriptionEnd(data?.subscription_end ?? null);
        subLastFetch.current = Date.now();
      } catch {
        setIsPro(false);
        setSubscriptionEnd(null);
      } finally {
        subInFlight.current = null;
      }
    })();
    subInFlight.current = promise;
    return promise;
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.pathname === '/') {
      setLoading(false);
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Fire welcome email on sign-in. Function is idempotent (checks email_send_log)
        // so this safely covers OAuth (Google) where the redirect kills any post-signup
        // invoke from Signup.tsx, as well as repeat sign-ins (no-op if already sent).
        if (event === 'SIGNED_IN' && session?.user?.email) {
          supabase.functions
            .invoke('send-welcome-email', {
              body: { email: session.user.email, user_id: session.user.id },
            })
            .catch((err) => console.warn('[252signal] welcome email enqueue failed', err));
          // Idempotent (PK + ignoreDuplicates) — safe even if SIGNED_IN fires repeatedly.
          void persistSignupAttribution(session.user.id);
        }
      }
    );

    supabase.auth.getSession().then(async ({ data: { session }, error }) => {
      if (error && (error as { code?: string }).code === 'refresh_token_not_found') {
        await supabase.auth.signOut();
        setSession(null);
        setUser(null);
        setLoading(false);
        return;
      }

      setSession(session ?? null);
      setUser(session?.user ?? null);
      setLoading(false);
    }).catch(async (error: unknown) => {
      if ((error as { code?: string }).code === 'refresh_token_not_found') {
        await supabase.auth.signOut();
        setSession(null);
        setUser(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Check subscription when user changes
  const lastUserId = useRef<string | null>(null);
  useEffect(() => {
    if (user) {
      // Force a fresh fetch when user identity changes (login or switch); TTL-cached otherwise.
      const userChanged = lastUserId.current !== user.id;
      lastUserId.current = user.id;
      checkSubscription(userChanged);
      const interval = setInterval(() => checkSubscription(true), 300_000);
      const onFocus = () => checkSubscription(true);
      window.addEventListener('focus', onFocus);
      return () => {
        clearInterval(interval);
        window.removeEventListener('focus', onFocus);
      };
    } else {
      lastUserId.current = null;
      subLastFetch.current = 0;
      setIsPro(false);
      setSubscriptionEnd(null);
    }
  }, [user, checkSubscription]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isPro, subscriptionEnd, checkSubscription, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
